/**
 * ContactCell — renders, in priority order: the entry-level override (tinted
 * so staff know it's not the creator's profile address), else the profile
 * contact with its source as a tooltip. With neither, a row nobody has searched
 * shows only a Find contact button that tracks its own hunt ("Looking…", then
 * "Search failed" on error); after a search that found nothing it adds
 * "No email found", the add-email trigger. No send control lives
 * here for the no-contact case — 2.2 never emails a creator, only sources and
 * tracks contact info.
 *
 * Both affordances need outreach:write on the BE (POST …/outreach/find-contacts
 * and PATCH …/outreach/contact), so without it the cell states the fact and
 * stops there rather than offering a button that can only 403.
 *
 * Once an address exists (override or profile), the whole cell becomes a
 * single send-sidebar trigger for just this one creator (onSendFirstTouch,
 * reusing the same bulk-send wizard one target at a time) — a `role="button"`
 * wrapper, not a real `<button>`, because the profile-contact branch still
 * nests its own "Change address" edit button (real `<button>`, stops its
 * own propagation) inside it; a real `<button>` can't contain another one.
 * A no-contact row only offers Find contact / the add-email trigger, never the send
 * trigger, and there's no way to resolve contact from inside the wizard
 * itself.
 */
import type { KeyboardEvent, ReactNode } from 'react';
import type { FindContactPhase } from '@discovr/features';
import type { OutreachRow } from '@discovr/types';
import { MagnifyingGlass } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { lookupNote } from './contactLookup';
import { sourceLabel } from './emailSources';
import { isRemovedRow } from './handoffStates';
import { canSendFirstTouch } from './RowActions';

const LINK_BUTTON = 'text-caption-medium text-[var(--brand-500)] hover:underline';
const NO_EMAIL_CLASS = 'inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-(--foreground-warning-default)';

export interface ContactCellProps {
  row: OutreachRow;
  canWrite: boolean;
  onFindContact: (entryId: string) => void;
  onEditContact: (entryId: string) => void;
  onSendFirstTouch: (entryId: string) => void;
  /** Undefined unless a Find contact for this row is in flight or just failed; a found
   *  contact needs no phase — the row itself switches to the contact branch. */
  findContactPhase?: FindContactPhase;
  /** Why the last search failed, in plain words; "Search failed" when absent. */
  findContactFailure?: string;
}

/** A contactable row's cell is one big send-sidebar trigger for that one
 *  creator — a plain, non-interactive wrapper without outreach:write, or
 *  once the row isn't eligible for a first touch any more (canSendFirstTouch
 *  — already in conversation, or a send already queued/sending). A second
 *  "first touch" would be a duplicate email, not a retry (matches
 *  RowActions' own "Send first touch" menu item gating verbatim). */
function ContactBlock({
  sendable,
  name,
  entryId,
  isOverride,
  onSendFirstTouch,
  children,
}: {
  sendable: boolean;
  name: string;
  entryId: string;
  /** The override tag/tooltip is visible to sighted users but wouldn't
   *  otherwise reach screen readers once aria-label replaces the block's
   *  accessible name — folded in here instead. */
  isOverride?: boolean;
  onSendFirstTouch: (entryId: string) => void;
  children: ReactNode;
}) {
  if (!sendable) {
    return <div className="flex min-w-0 flex-col gap-0.5">{children}</div>;
  }
  const trigger = () => onSendFirstTouch(entryId);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    trigger();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={isOverride ? `Send to ${name} (override address)` : `Send to ${name}`}
      onClick={(e) => {
        e.stopPropagation();
        trigger();
      }}
      onKeyDown={onKeyDown}
      className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-left"
    >
      {children}
    </div>
  );
}

export function ContactCell({ row, canWrite: canWriteRow, onFindContact, onEditContact, onSendFirstTouch, findContactPhase, findContactFailure }: ContactCellProps) {
  // E9-144: a removed row takes no contact work; Reopen brings it back first.
  const canWrite = canWriteRow && !isRemovedRow(row);
  const name = row.displayName || row.username;
  const sendable = canWrite && canSendFirstTouch(row);

  if (row.emailOverride) {
    return (
      <ContactBlock sendable={sendable} name={name} entryId={row.entryId} isOverride onSendFirstTouch={onSendFirstTouch}>
        <span
          className="w-fit max-w-full truncate rounded-sm bg-(--brand-100) px-1 text-body-sm-regular text-foreground"
          title="This entry uses an override address instead of the creator's profile email"
        >
          {row.emailOverride}
        </span>
      </ContactBlock>
    );
  }

  if (row.hasContact && row.email) {
    const sourceTitle = row.emailSource ? sourceLabel(row.emailSource) : undefined;
    return (
      <ContactBlock sendable={sendable} name={name} entryId={row.entryId} onSendFirstTouch={onSendFirstTouch}>
        <span className="truncate text-body-sm-regular text-foreground" title={sourceTitle}>{row.email}</span>
        {canWrite && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditContact(row.entryId);
            }}
            className="w-fit text-caption-medium text-muted-foreground hover:underline"
          >
            Change address
          </button>
        )}
      </ContactBlock>
    );
  }

  const note = lookupNote(row);
  // A re-search in flight has not answered yet, so the last "none found" no longer stands.
  const searchedNone = row.outreachContact === 'not_found' && findContactPhase !== 'looking';
  // Owner ruling 2026-10-09: every row without an address offers Add email beside Find contact —
  // before a search, during one, and when it can never be searched. After a search found nothing,
  // "No email found" is the trigger.
  const addLabel = searchedNone ? 'No email found' : 'Add email';
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {canWrite && (
        <button
          type="button"
          title="Add an email address"
          aria-label={searchedNone ? `No email found for ${name}. Add an email` : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onEditContact(row.entryId);
          }}
          className={cn(NO_EMAIL_CLASS, 'hover:underline focus-visible:underline')}
        >
          {addLabel}
        </button>
      )}
      {searchedNone && !canWrite && <span className={NO_EMAIL_CLASS}>No email found</span>}
      {note && <span className="text-caption-regular text-muted-foreground">{note}</span>}
      {canWrite && !note && (
        <FindContactControl entryId={row.entryId} name={name} phase={findContactPhase} failure={findContactFailure} searchedNone={searchedNone} onFindContact={onFindContact} />
      )}
    </div>
  );
}

/** aria-disabled, not disabled, while looking: a natively disabled button drops focus mid-click.
 *  The status region stays mounted so "Search failed" is announced — a region inserted at
 *  announcement time usually isn't. Its text names the creator: several rows can announce at once. */
function FindContactControl({
  entryId,
  name,
  phase,
  failure,
  searchedNone,
  onFindContact,
}: {
  entryId: string;
  name: string;
  phase?: FindContactPhase;
  failure?: string;
  /** Announced from this same region, so the search's end is heard where "Looking…" was. */
  searchedNone: boolean;
  onFindContact: (entryId: string) => void;
}) {
  const looking = phase === 'looking';
  return (
    <span className="inline-flex items-center">
      <button
        type="button"
        aria-disabled={looking || undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!looking) onFindContact(entryId);
        }}
        className={cn(LINK_BUTTON, 'inline-flex items-center gap-1', looking && 'cursor-default no-underline hover:no-underline')}
      >
        {looking && <MagnifyingGlass size={12} aria-hidden="true" className="motion-safe:animate-pulse" />}
        {looking ? 'Looking…' : 'Find contact'}
      </button>
      <span role="status" className="text-caption-medium text-rose-700">
        {looking && <span className="sr-only">Looking for {name}'s email</span>}
        {searchedNone && <span className="sr-only">No email found for {name}</span>}
        {phase === 'failed' && (
          <>
            <span className="ml-2">{failure ?? 'Search failed'}</span>
            <span className="sr-only"> for {name}</span>
          </>
        )}
      </span>
    </span>
  );
}
